import React from 'react';
import { useTranslation } from 'react-i18next';

export const PAGE_SIZE = 10;

export type SortDirection = 'asc' | 'desc';

/** Gradient-header column button with up/down carets. */
export const SortableHeader: React.FC<{
    label: string;
    className: string;
    active: boolean;
    direction: SortDirection;
    onSort: () => void;
}> = ({ label, className, active, direction, onSort }) => (
    <button
        type="button"
        onClick={onSort}
        // aria-sort belongs on a columnheader, not a button; announce the
        // current sort state to screen readers instead.
        aria-label={active
            ? `${label}, sorted ${direction === 'asc' ? 'ascending' : 'descending'}`
            : `${label}, not sorted`}
        className={`${className} flex items-center gap-1.5 text-left uppercase tracking-wider font-black hover:text-cyan-50 transition-colors`}
    >
        {label}
        <span className="flex flex-col leading-none text-[13px]">
            <i className={`fa-solid fa-caret-up ${active && direction === 'asc' ? 'text-white' : 'text-white/50'}`}></i>
            <i className={`fa-solid fa-caret-down -mt-1 ${active && direction === 'desc' ? 'text-white' : 'text-white/50'}`}></i>
        </span>
    </button>
);

/** "Showing X of Y" footer with Previous / page numbers / Next. */
export const Pagination: React.FC<{
    page: number;
    totalPages: number;
    shown: number;
    total: number;
    onPage: (page: number) => void;
}> = ({ page, totalPages, shown, total, onPage }) => {
    const { t } = useTranslation();
    const navClass = 'px-3 py-1.5 bg-white border border-cyan-200 rounded text-sm font-medium text-cyan-700 hover:bg-cyan-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
    return (
        <div className="px-6 py-4 border-t border-cyan-100 bg-cyan-50/30 flex items-center justify-between">
            <div className="text-sm text-slate-500">
                {t('users.showingResults', { shown, total })}
            </div>
            {totalPages > 1 && (
                <div className="flex gap-2">
                    <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1} className={navClass}>
                        {t('users.previous')}
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                            key={p}
                            onClick={() => onPage(p)}
                            className={
                                p === page
                                    ? 'px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded text-sm font-medium'
                                    : 'px-3 py-1.5 bg-white border border-cyan-200 rounded text-sm font-medium text-cyan-700 hover:bg-cyan-50 transition-colors'
                            }
                        >
                            {p}
                        </button>
                    ))}
                    <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className={navClass}>
                        {t('users.next')}
                    </button>
                </div>
            )}
        </div>
    );
};
